<script setup lang="ts">
import { useToast } from 'primevue/usetoast';
import { useField, useForm } from 'vee-validate';
import { ref } from 'vue';

import { CreateUserZodSchema } from '../Schemas/CreateUserZodValidationSchema';
import { validateSchema } from '../Schemas/Schemas';

const createUserFormValidationSchema = validateSchema(CreateUserZodSchema);

const { handleSubmit, errors, resetForm } = useForm<{
  email: string;
  password: string;
  directoryName: string;
}>({
  validationSchema: createUserFormValidationSchema,
  validateOnMount: false,
});

const toast = useToast();

const { value: email } = useField<string>('email');

const { value: password } = useField<string>('password');

const { value: directoryName } = useField<string>('directoryName');

const createUserError = ref<string | null>(null);

const onSubmit = (action: string) => {
  handleSubmit(async (): Promise<void> => {
    try {
      if (action === 'create') {
        create();
        return;
      } else {
        update();
      }

      resetForm();
    } catch (error) {
      console.error(error);
      createUserError.value = error as string;
    }
  })();
};

const create = async (): Promise<void> => {
  try {
    toast.add({
      closable: true,
      detail: 'Użytkownik został utworzony',
      severity: 'success',
      summary: `Sukces - użytkownik ${email.value} został utworzony`,
    });
  } catch (error) {
    console.error(error);
    createUserError.value = error as string;
  }
};

const update = async (): Promise<void> => {
  try {
    toast.add({
      closable: true,
      detail: 'Użytkownik został zaktualizowany',
      severity: 'success',
      summary: `Sukces - użytkownik ${email.value} został zaktualizowany`,
    });
  } catch (error) {
    console.error(error);
    createUserError.value = error as string;
  }
};
</script>

<template>
  <div class="form-container">
    <form
      class="form"
      action="#"
      @submit.prevent=""
    >
      <div class="title">Zarządaj użytkownikami</div>
      <div class="input-container">
        <span class="p-float-label input-wrapper">
          <InputText
            id="email"
            v-model="email"
            class="input"
            :class="{ 'p-invalid': errors.email }"
          />
          <label
            class="label"
            for="email"
            >Login</label
          >
        </span>
        <small
          id="text-error"
          class="error-message"
        >
          {{ errors.email || '&nbsp;' }}
        </small>
      </div>
      <div class="input-container">
        <span class="p-float-label input-wrapper">
          <InputText
            id="directoryName"
            v-model="directoryName"
            class="input"
            :class="{ 'p-invalid': errors.directoryName }"
          />
          <label
            class="label"
            for="directoryName"
            >Nazwa katalogu</label
          >
        </span>
        <small
          id="text-error"
          class="error-message"
        >
          {{ errors.directoryName || '&nbsp;' }}
        </small>
      </div>
      <div class="input-container">
        <span class="p-float-label input-wrapper">
          <InputText
            id="password"
            v-model="password"
            input-id="password"
            :input-class="'input'"
            :feedback="false"
            type="text"
            class="input"
            :class="{ 'p-invalid': errors.password }"
            aria-describedby="text-error"
            @input="password = ($event?.target as HTMLInputElement)?.value"
          />
          <label
            class="label"
            unstyled
            for="password"
            >Hasło</label
          >
        </span>
        <small
          id="text-error"
          class="error-message"
        >
          {{ errors.password || '&nbsp;' }}
        </small>
      </div>
      <div class="buttons">
        <Button
          class="submit-button"
          severity="primary"
          type="submit"
          @click="onSubmit('create')"
          >Utwórz</Button
        >
        <Button
          class="submit-button"
          severity="primary"
          type="submit"
          @click="onSubmit('update')"
          >Zaaktualizuj</Button
        >
      </div>
      <small
        id="submit-error"
        class="error-message"
      >
        {{ createUserError || '&nbsp;' }}
      </small>
    </form>
    <Toast
      position="bottom-left"
      :unstyled="true"
    />
  </div>
</template>

<style lang="scss" scoped>
.form-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  background-color: #fffbf8;
}

.form {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: 2rem;
  padding: 2rem;
}

.title {
  color: black;
  font-size: 2.5rem;
  text-align: center;
  flex: 1;
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 50%;
  min-width: 250px;
}

.input-wrapper {
  width: 100%;
}

.input {
  padding: 1rem;
  border-radius: 0.5rem;
  width: 100%;
  min-width: 150px;
}

.label {
  margin-top: -0.5rem;
  color: rgb(0 0 0 / 70%);
}

.error-message {
  color: red;
}

.submit-button {
  font-size: 1.75rem;
  width: 17rem;
  height: 4rem;
}

.buttons {
  width: 75%;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-evenly;
  gap: 2rem;
}
</style>
